import React, {
    useState, useEffect, useRef, useImperativeHandle, forwardRef
} from 'react';
import ThumbnailMediaItem from 'components/ProductPage/ProductMediaCarousel/ThumbnailMediaItem';
import { fontSizes } from 'style/config';
import { Link, Divider } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import { THUMB_COUNT } from 'components/ProductPage/ProductMediaCarousel/constants';
import ThumbnailCarouselEN from 'components/ProductPage/ProductMediaCarousel/ThumbnailCarousel/locales/ThumbnailCarousel_en_US';
import ThumbnailCarouselFR from 'components/ProductPage/ProductMediaCarousel/ThumbnailCarousel/locales/ThumbnailCarousel_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ThumbnailCarouselEN, ThumbnailCarouselFR);

const ThumbnailCarousel = forwardRef(({ seeAllClick, items, productId, carouselRef }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef(null);

    useImperativeHandle(
        ref,
        () => ({
            scrollTo: index => {
                setSelectedIndex(index);
                const listElem = listRef.current;

                if (listElem) {
                    listElem.scrollTo(0, listElem.offsetHeight * Math.floor(index / THUMB_COUNT));
                }
            }
        }),
        []
    );

    useEffect(() => {
        carouselRef?.current?.scrollTo(0);
        setSelectedIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    useEffect(() => {
        const listElem = listRef.current;

        if (listElem) {
            listElem.scrollTo(0, listElem.offsetHeight * Math.floor(selectedIndex / THUMB_COUNT));
        }
    }, [selectedIndex]);

    const handleOnClick = currentIndex => {
        carouselRef?.current.scrollTo(currentIndex);
        setSelectedIndex(currentIndex);
    };

    const pagesCount = Math.ceil(items.length / THUMB_COUNT);

    return (
        <>
            <div css={styles.listWrap}>
                <ul
                    ref={listRef}
                    css={styles.list}
                >
                    {Array.from(Array(pagesCount).keys()).map((page, pageIndex) => (
                        <li
                            key={pageIndex.toString()}
                            css={styles.item}
                            data-at={Sephora.debug.dataAt('product_images')}
                        >
                            <div css={styles.itemInner}>
                                {items.slice(page * THUMB_COUNT, (page + 1) * THUMB_COUNT).map((item, index) => (
                                    <ThumbnailMediaItem
                                        key={index.toString()}
                                        index={page * THUMB_COUNT + index}
                                        selected={page * THUMB_COUNT + index === selectedIndex}
                                        onClick={handleOnClick}
                                        children={item}
                                    />
                                ))}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            {items.length > THUMB_COUNT && (
                <>
                    <Divider
                        marginTop={4}
                        marginBottom={2}
                    />
                    <Link
                        fontSize='sm'
                        paddingY={2}
                        marginY={-2}
                        width='100%'
                        onClick={seeAllClick(selectedIndex)}
                        display='block'
                        textAlign='center'
                        data-at={Sephora.debug.dataAt('see_all_images_btn')}
                    >
                        {getText('seeAll')}
                        <br />
                        <span
                            css={{ fontSize: fontSizes.base }}
                            children={items.length}
                        />
                    </Link>
                </>
            )}
        </>
    );
});

ThumbnailCarousel.displayName = 'ThumbnailCarousel';

const styles = {
    listWrap: {
        position: 'relative',
        overflow: 'hidden',
        marginTop: -4,
        paddingBottom: `${THUMB_COUNT * 100}%`
    },
    list: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        scrollBehavior: 'smooth'
    },
    item: {
        position: 'relative',
        height: '100%'
    },
    itemInner: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
    }
};

// Note: not wrapped with wrapFunctionalComponent because its internal renderWrapper +
// React.memo do not forward refs. Parent (ProductMediaCarousel) requires an imperative
// ref to call scrollTo() for main-carousel -> thumbnail sync.
export default React.memo(ThumbnailCarousel);
